import React, {FC, ReactNode} from 'react';
import {Trans} from '@deviceinsight/ng-ui-scale-lib/api';
import classNames from 'classnames';
import {Button, Icon} from '@deviceinsight/ng-ui-basic-components';
import type {AssetGroup} from '@deviceinsight/ng-ui-api-client';
import type {UiCondition} from '../../../modules/ruleengine/types';
import RuleConditionEdit from './RuleConditionEdit';
import {createCondition, shouldShowSettingsHeader} from '../ruleEditHelpers';
import RuleComplexConditionEdit from './RuleComplexConditionEdit';
import TriggerShowDetailsButton from './TriggerShowDetailsButton';
import InfoText from '../../common/InfoText';
import hasAuthority from '../../common/hasAuthority';
import {AUTHORITY_READ_RULE_OPTIONAL_SETTING} from '../../../modules/common/consts';
export type Props = {
	conditions: UiCondition | null | undefined;
	onChange: (arg0: UiCondition | null | undefined) => void;
	onOpenTriggerDetails?: () => void;
	assetGroup: AssetGroup;
	addLabel: ReactNode;
	title: ReactNode;
	isTrigger: boolean;
	isAddable: boolean;
	canEdit: boolean;
};

const RuleConditionsContainer: FC<Props> = ({
	conditions,
	onChange,
	onOpenTriggerDetails,
	addLabel,
	title,
	isTrigger,
	assetGroup,
	isAddable,
	canEdit
}) => {
	const addCondition = () => {
		const addedCondition = createCondition(undefined);
		onChange(addedCondition);
	};

	const updateCondition = (updatedCondition: UiCondition) => {
		onChange(updatedCondition);
	};

	const removeCondition = () => {
		onChange(undefined);
	};

	const showSettingsHeader = shouldShowSettingsHeader(conditions);
	const canSeeRuleOptionalSettings = hasAuthority(AUTHORITY_READ_RULE_OPTIONAL_SETTING);
	return (
		<div className="rule-condition-container">
			<h3>{title}</h3>
			<div
				className={classNames('optional-settings-header', {
					disabled: !canSeeRuleOptionalSettings
				})}>
				{isTrigger && showSettingsHeader && (
					<Trans i18nKey="ruleengine.edit.rule.optionalSettings">Optional Settings</Trans>
				)}
			</div>
			{conditions &&
				(conditions.nestedConditions && conditions.nestedConditions.length ? (
					<RuleComplexConditionEdit
						assetGroup={assetGroup}
						condition={conditions}
						onChange={updateCondition}
						index={0}
						canEdit={canEdit}
						key={conditions.metadata.uuid}
						onRemove={removeCondition}
						isTrigger={isTrigger}
						isAddable={isAddable}
					/>
				) : (
					<RuleConditionEdit
						assetGroup={assetGroup}
						condition={conditions}
						onChange={updateCondition}
						index={0}
						canEdit={canEdit}
						key={conditions.metadata.uuid}
						onRemove={removeCondition}
						isTrigger={isTrigger}
						isAddable={isAddable}
					/>
				))}
			{isTrigger && showSettingsHeader && (
				<div
					className={classNames('trigger-details-link', {
						'disabled-link': !onOpenTriggerDetails,
						disabled: !canSeeRuleOptionalSettings
					})}>
					<InfoText
						bubble={{
							id: 'ruleengine.edit.rule.triggerDetailsLinkHelp',
							defaultValue: 'Only clickable if the rule is saved and optional settings were chosen'
						}}>
						<TriggerShowDetailsButton
							onClick={
								onOpenTriggerDetails
									? onOpenTriggerDetails
									: () => {
											//
									  }
							}
						/>
					</InfoText>
				</div>
			)}
			{!conditions && canEdit && (
				<div className="condition-add">
					<Button naked onClick={addCondition}>
						{addLabel}
						<Icon add />
					</Button>
				</div>
			)}
		</div>
	);
};

export default RuleConditionsContainer;
